/**
 * User Activity Timeline Data Hook
 * Fetches and transforms user activity data for timeline display
 */

import { useMemo, useEffect } from 'react'
import dayjs from 'dayjs'
import { sortWith, descend } from 'ramda'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import type {
  TimelineGroup,
  TimelineEventData,
} from 'src/v3/components/timeline/ModularTimeline'
import type {
  RosterUserActivityEvent,
  RosterUserActivityResponse,
  RosterUserTimelineQueryParams,
} from '../types'
import {
  fetchSelectedUserActivity,
  selectSelectedUserActivityStatus,
  selectSelectedUserActivity,
} from '../../manage.slice'

/**
 * Hook parameters
 */
interface UseUserActivityTimelineParams {
  userID: string
  linkedServiceID: string
  queryParams?: RosterUserTimelineQueryParams
  enabled?: boolean
}

/**
 * Activity color mapping based on theme.ts entityColors - matching Manage Dashboard
 * Constant to avoid recreating on every transform
 */
let ACTIVITY_COLORS: Record<string, string> = {
  // All CE activities use same teal color (matching dashboard logic)
  recorded: '#02B2AF', // entityColors.ceActivity
  live: '#02B2AF', // entityColors.ceActivity
  external: '#02B2AF', // entityColors.ceActivity
  'certificate-recorded': '#02B2AF', // entityColors.ceActivity (legacy)
  'certificate-live': '#02B2AF', // entityColors.ceActivity (legacy)
  'certificate-external': '#02B2AF', // entityColors.ceActivity (legacy)

  // Assignments
  assignment: '#72CCFF', // entityColors.assignment
  'assignment-created': '#72CCFF', // entityColors.assignment (legacy)
  'assignment-completed': '#72CCFF', // entityColors.assignment (legacy)

  // User actions
  'user-action': '#3B48E0', // entityColors.extra1
  'event-registered': '#3B48E0', // entityColors.extra1 (legacy)
  'event-attended': '#3B48E0', // entityColors.extra1 (legacy)
  'event-completed': '#3B48E0', // entityColors.extra1 (legacy)

  // Credential activities
  'credential-activity': '#2E96FF', // entityColors.credentialActivity
  'credential-uploaded': '#2E96FF', // entityColors.credentialActivity (legacy)
  'credential-expiring': '#2E96FF', // entityColors.credentialActivity (legacy)
  'credential-expired': '#2E96FF', // entityColors.credentialActivity (legacy)

  // License activities
  'license-activity': '#9001CB', // entityColors.licenseActivity
  'license-uploaded': '#9001CB', // entityColors.licenseActivity (legacy)
  'license-expiring': '#9001CB', // entityColors.licenseActivity (legacy)
  'license-expired': '#9001CB', // entityColors.licenseActivity (legacy)
}

/**
 * Event type to category mapping for O(1) filtering
 */
let EVENT_TYPE_CATEGORY_MAP: Record<string, string> = {
  // CE Activities
  recorded: 'ce-activity',
  live: 'ce-activity',
  external: 'ce-activity',
  'certificate-recorded': 'ce-activity',
  'certificate-live': 'ce-activity',
  'certificate-external': 'ce-activity',

  // Assignments
  assignment: 'assignment',
  'assignment-created': 'assignment',
  'assignment-completed': 'assignment',

  // User Actions
  'user-action': 'user-action',
  'event-registered': 'user-action',
  'event-attended': 'user-action',
  'event-completed': 'user-action',

  // Credential Activities
  'credential-activity': 'credential-activity',
  'credential-uploaded': 'credential-activity',
  'credential-expiring': 'credential-activity',
  'credential-expired': 'credential-activity',

  // License Activities
  'license-activity': 'license-activity',
  'license-uploaded': 'license-activity',
  'license-expiring': 'license-activity',
  'license-expired': 'license-activity',
}

/**
 * Transform RosterUserActivityEvent to TimelineEventData for ModularTimeline
 */
let transformActivityEvent = (
  event: RosterUserActivityEvent,
): TimelineEventData => {
  let color = ACTIVITY_COLORS[event.type] || '#02B2AF'

  return {
    id: event.id,
    title: event.title,
    description: event.description,
    timestamp: event.timestamp,
    type: event.type,
    color,
    metadata: event.metadata,
    actions: event.actions?.map((action) => ({
      label: action.label,
      onClick: () => {
        // Action handling will be done by parent component
      },
      color: action.variant === 'download' ? 'primary' : 'secondary',
    })),
    data: event, // Pass full event for modal access
  }
}

/**
 * Group activities by date for timeline display
 */
let groupActivitiesByDate = (
  events: RosterUserActivityEvent[],
): TimelineGroup[] => {
  let transformedEvents = events.map(transformActivityEvent)

  // Group events by date
  let groupedByDate = transformedEvents.reduce(
    (groups, event) => {
      let date = event.timestamp
        ? dayjs(event.timestamp).format('YYYY-MM-DD')
        : 'unknown'
      let existingEvents = groups[date] || []
      return {
        ...groups,
        [date]: [...existingEvents, event],
      }
    },
    {} as Record<string, TimelineEventData[]>,
  )

  // Convert to TimelineGroup array, sorted by date (newest first)
  let timelineGroups = Object.entries(groupedByDate).map(
    ([date, dateEvents]) => ({
      date,
      events: sortWith(
        [
          descend((event: TimelineEventData) => {
            if (!event.timestamp) return 0
            return dayjs(event.timestamp).valueOf()
          }),
        ],
        dateEvents,
      ),
      totalEvents: dateEvents.length,
    }),
  )

  return sortWith(
    [descend((group: TimelineGroup) => dayjs(group.date).valueOf())],
    timelineGroups,
  )
}

// Removed fetchSelectedUserActivity function - now handled by Redux thunk

/**
 * User Activity Timeline Hook
 */
export function useUserActivityTimeline({
  userID,
  linkedServiceID,
  queryParams,
  enabled = true,
}: UseUserActivityTimelineParams) {
  let dispatch = useAppDispatch()

  // Use query params if provided, otherwise use default date range
  let finalStartDate =
    queryParams?.startDate || dayjs().subtract(30, 'days').format('YYYY-MM-DD')
  let finalEndDate = queryParams?.endDate || dayjs().format('YYYY-MM-DD')

  // Get data from Redux store
  let activityStatus = useAppSelector(selectSelectedUserActivityStatus)
  let activityData = useAppSelector(
    selectSelectedUserActivity,
  ) as RosterUserActivityResponse | null

  // Fetch data effect
  useEffect(() => {
    if (enabled && userID && linkedServiceID) {
      dispatch(
        fetchSelectedUserActivity({
          userID,
          linkedServiceID,
          startDate: finalStartDate,
          endDate: finalEndDate,
        }),
      )
    }
  }, [dispatch, userID, linkedServiceID, finalStartDate, finalEndDate, enabled])

  // Transform and filter activities (matching dashboard pattern exactly)
  let filteredActivities = useMemo(() => {
    // Handle both possible data structures: activities or events
    let events = activityData?.activities || activityData?.events || []
    if (events.length === 0) return []

    // Pre-compute filter lookups for O(1) access instead of O(n) array.includes
    let searchKeyword = queryParams?.searchKeyword?.toLowerCase()
    let categorySet = queryParams?.categories?.length
      ? new Set(queryParams.categories)
      : null
    let activityTypeSet = queryParams?.activityTypes?.length
      ? new Set(queryParams.activityTypes)
      : null
    let ceActivityTypes = new Set([
      'recorded',
      'live',
      'external',
      'certificate-recorded',
      'certificate-live',
      'certificate-external',
    ])

    // Apply frontend filters exactly like dashboard
    return events.filter((event: RosterUserActivityEvent) => {
      // Filter by keyword search (case-insensitive search on title)
      if (searchKeyword) {
        let title = event.title?.toLowerCase() || ''
        if (!title.includes(searchKeyword)) {
          return false
        }
      }

      // Filter out license and credential events when toggle is off
      if (queryParams?.showLicenseCredentialEvents === false) {
        if (
          event.type.startsWith('credential-') ||
          event.type.startsWith('license-')
        ) {
          return false
        }
      }

      // Filter by categories - match dashboard logic (only for CE activities)
      if (categorySet) {
        // Check if this is a CE activity based on event.type
        if (ceActivityTypes.has(event.type)) {
          // Try to get category from metadata (since user timeline uses metadata structure)
          let categoryMetadata = event.metadata?.find(
            (m: { key: string; value: string }) =>
              m.key === 'category' ||
              m.key === 'Category' ||
              m.key === 'Course Category',
          )
          if (categoryMetadata && !categorySet.has(categoryMetadata.value)) {
            return false
          }
        }
        // Non-CE activities (assignments, etc.) pass through like in dashboard
      }

      // Filter by activity types - O(1) lookup using category map
      if (activityTypeSet) {
        let category = EVENT_TYPE_CATEGORY_MAP[event.type]
        if (!category || !activityTypeSet.has(category)) {
          return false
        }
      }

      return true
    })
  }, [
    activityData?.activities,
    activityData?.events,
    queryParams?.searchKeyword,
    queryParams?.categories,
    queryParams?.activityTypes,
    queryParams?.showLicenseCredentialEvents,
  ])

  // Transform and group events
  let timelineGroups = useMemo(() => {
    if (filteredActivities.length === 0) return []
    return groupActivitiesByDate(filteredActivities)
  }, [filteredActivities])

  // Calculate statistics based on filtered events
  let statistics = useMemo(() => {
    if (filteredActivities.length === 0) {
      return {
        totalEvents: 0,
        totalDays: 0,
        eventBreakdown: {},
        filteredCount: 0,
        totalCount: (activityData?.activities || activityData?.events || [])
          .length,
      }
    }

    let eventBreakdown = filteredActivities.reduce(
      (breakdown: Record<string, number>, event: RosterUserActivityEvent) => ({
        ...breakdown,
        [event.type]: (breakdown[event.type] || 0) + 1,
      }),
      {} as Record<string, number>,
    )

    return {
      totalEvents: filteredActivities.length,
      totalDays: timelineGroups.length,
      eventBreakdown,
      filteredCount: filteredActivities.length,
      totalCount: (activityData?.activities || activityData?.events || [])
        .length,
    }
  }, [
    filteredActivities,
    timelineGroups.length,
    activityData?.activities,
    activityData?.events,
  ])

  // Refetch function
  let refetch = () => {
    if (userID && linkedServiceID) {
      dispatch(
        fetchSelectedUserActivity({
          userID,
          linkedServiceID,
          startDate: finalStartDate,
          endDate: finalEndDate,
        }),
      )
    }
  }

  return {
    // Data
    timelineGroups,
    events: filteredActivities,
    allEvents: activityData?.activities || activityData?.events || [],
    pagination: activityData?.pagination,
    statistics,

    // States
    isLoading: activityStatus === 'pending',
    isError: activityStatus === 'rejected',
    error:
      activityStatus === 'rejected'
        ? new Error('Failed to fetch activity timeline')
        : null,

    // Actions
    refetch,
  }
}
